import { Fragment, useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { UseFormRegister, UseFormWatch, UseFormSetValue } from 'react-hook-form';
import { IManualMessageInfo } from './ManualMessage';
import { scrollBarStyle } from 'constants/theme';
import DatePickerPopup from 'components/Popup/DatePicker';
import { useCheckedCustomerInfos } from './ManualStore';
import { ReactComponent as IcDateFill } from 'constants/icon/ic_date_fill_gray.svg';
import IndeterminateCheckbox from 'components/Table/util/IndeterminateCheckbox';
import useToastStore from 'components/Toast/ToastStore';
import { useIsDifferenceStore } from '../common/MessagePromptStore';
import { Text } from 'components/Typography';

type Props = {
  register: UseFormRegister<IManualMessageInfo>;
  watch: UseFormWatch<IManualMessageInfo>;
  setValue: UseFormSetValue<IManualMessageInfo>;
};

const ManualMessageComponent = ({ register, watch, setValue }: Props) => {
  const { setIsDifference } = useIsDifferenceStore();
  const { addToast } = useToastStore();
  const { checkedCustomerInfos } = useCheckedCustomerInfos();
  const [isOpenCalander, setIsOpenCalander] = useState(false);

  const [isToastShown, setIsToastShown] = useState(false);

  const isChanging = useMemo(() => {
    return checkedCustomerInfos.filter(item => item.customer_phone.length !== 0).length > 0;
  }, [checkedCustomerInfos]);

  const encodedLength = useMemo(() => {
    const encoder = new TextEncoder();
    const encoded = encoder.encode(watch('contents'));
    return encoded.length;
  }, [watch('contents')]);

  const isOver90Length = useMemo(() => {
    return encodedLength > 90;
  }, [encodedLength]);

  return (
    <Fragment>
      <FirstDiv>
        <FirstDivTitle>
          <div>1.메시지 제목</div>
          <SecondDivTitleDescriptionDiv>90byte 이하의 단문메시지는 제목이 미발송됩니다.</SecondDivTitleDescriptionDiv>
        </FirstDivTitle>
        <FirstDivTitleInput
          value={watch('title')}
          onChange={e => {
            setIsDifference(true);
            return setValue('title', e.target.value);
          }}
          placeholder="제목을 입력해주세요."
        />
      </FirstDiv>
      <SecondDiv>
        <SecondDivTitle>
          <div>2.메시지 내용</div>
          <SecondDivTitleDescriptionDiv>
            <div>90byte 초과시 장문메시지로 자동 변환합니다.</div>
            <div style={{ display: 'flex' }}>
              {isOver90Length && <LMSLabel>LMS</LMSLabel>}(
              {isOver90Length && <SecondDivOver90Text>{encodedLength}</SecondDivOver90Text>}
              {!isOver90Length && encodedLength}
              /90byte)
            </div>
          </SecondDivTitleDescriptionDiv>
        </SecondDivTitle>
        <SecondDivCententContainer isTextArea={isChanging}>
          {isChanging ? (
            <SecondDivCententTextArea
              placeholder="메시지 내용을 입력해주세요."
              value={watch('contents')}
              onChange={e => {
                setIsDifference(true);
                const encoder = new TextEncoder();
                const targetEncodedLength = encoder.encode(e.target.value).length;
                if (targetEncodedLength > 90 && !isToastShown) {
                  addToast('장문메시지(LMS)로 전환됐습니다.', 3000);
                  setIsToastShown(true);
                }
                if (targetEncodedLength <= 90) {
                  setIsToastShown(false);
                }
                return setValue('contents', e.target.value);
              }}
            />
          ) : (
            <Text
              styleName="body2"
              sx={{
                color: 'var(--GrayScale-gray700, #7D818D)',
                fontFeatureSettings: 'liga off, clig off',
                fontFamily: 'Pretendard',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '24px',
                letterSpacing: '-0.28px',
              }}
            >
              먼저 메시지를 보낼 고객을 주문 내역에서 체크해 주세요.
            </Text>
          )}
        </SecondDivCententContainer>
      </SecondDiv>
      <ThirdDiv>
        <ThirdDivTitleDiv>
          <ThirdDivTitleText>3.예약 날짜/시간 지정</ThirdDivTitleText>
          <ThirdDivTitleDescriptionText>메시지를 보낼 시간을 예약하거나 직접 발송합니다.</ThirdDivTitleDescriptionText>
        </ThirdDivTitleDiv>
        <ThirdDivDateContainer>
          <ThirdDivDateInputContainer isDisabled={!!watch('isSendNow')}>
            <CalanderIconContainer
              isActive={isOpenCalander}
              hasError={false}
              onClick={() => {
                if (!!!watch('isSendNow')) {
                  setIsOpenCalander(true);
                }
              }}
            >
              <IcDateFill />
            </CalanderIconContainer>
            <input
              readOnly
              onClick={() => {
                setIsDifference(true);
                return setIsOpenCalander(true);
              }}
              value={watch('sendDate')}
              placeholder="날짜를 선택해 주세요."
              disabled={!!watch('isSendNow')}
            />

            {isOpenCalander && (
              <div style={{ position: 'relative', top: -15, right: 390 }}>
                <DatePickerPopup
                  setIsOpen={setIsOpenCalander}
                  setValue={setValue}
                  initDate={watch('sendDate')}
                  name="sendDate"
                  format="YYYY.MM.DD"
                  banPast={true}
                />
              </div>
            )}
          </ThirdDivDateInputContainer>
          <TimeContaniner isDisabled={!!watch('isSendNow')}>
            <TimeInput
              type="number"
              color={!!watch('isSendNow') ? '#8F96A9' : '#000000'}
              value={watch('sendHour')}
              onChange={e => {
                const value = parseInt(e.target.value);
                if (value < 0) return setValue('sendHour', '00');
                if (value > 23) return setValue('sendHour', '23');
                else setValue('sendHour', value.toString().length === 1 ? `0${value}` : value.toString());
              }}
              disabled={!!watch('isSendNow')}
            />
            <TimeText>시</TimeText>
          </TimeContaniner>
          :
          <TimeContaniner isDisabled={!!watch('isSendNow')}>
            <TimeInput
              type="number"
              value={watch('sendMinute')}
              color={!!watch('isSendNow') ? '#8F96A9' : '#000000'}
              onChange={e => {
                const value = parseInt(e.target.value);
                if (value < 0) return setValue('sendMinute', '00');
                if (value > 59) return setValue('sendMinute', '59');
                else return setValue('sendMinute', value.toString().length === 1 ? `0${value}` : value.toString());
              }}
              disabled={!!watch('isSendNow')}
            />
            <TimeText>분</TimeText>
          </TimeContaniner>
        </ThirdDivDateContainer>
        <ThirdCheckBoxContainer>
          <div>
            <IndeterminateCheckbox
              checked={watch('isSendNow')}
              onChange={() => setValue('isSendNow', !watch('isSendNow'))}
              id="isSendNow"
            />
          </div>
          <div>
            <label htmlFor="isSendNow" style={{ cursor: 'pointer' }}>
              즉시 발송하기
            </label>
          </div>
        </ThirdCheckBoxContainer>
      </ThirdDiv>
      <FirthDiv>
        <FirthDivTitleWrapper>
          <FirthDivTitle>4.수신인</FirthDivTitle>
          <FirthDivDescription>메시지를 보낼 수신인을 왼쪽 표에서 선택(체크)할 수 있습니다.</FirthDivDescription>
        </FirthDivTitleWrapper>
        <FirthCustomerInfoContainer>
          {checkedCustomerInfos
            .filter(item => item.customer_phone.length !== 0)
            .map((customerInfo, index) => (
              <FirthCustomerInfoItem key={customerInfo.customer_phone}>
                {customerInfo.customer_name} / {formatPhoneNumber(customerInfo.customer_phone)}
              </FirthCustomerInfoItem>
            ))}
        </FirthCustomerInfoContainer>
      </FirthDiv>
    </Fragment>
  );
};

function formatPhoneNumber(phoneNumber: string) {
  // 앞자리가 2자리 또는 3자리일 수 있는 경우
  return phoneNumber.replace(/(\d{2,3})(\d{4})(\d{4})/, '$1-$2-$3');
}

export default ManualMessageComponent;

const FirstDiv = styled.div`
  display: flex;
  padding: 0px 20px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;
const FirstDivTitle = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;

  color: var(--RG02, #25282f);
  font-feature-settings: 'liga' off, 'clig' off;

  /* body16B */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px; /* 150% */
  letter-spacing: -0.32px;
`;
const FirstDivTitleInput = styled.input`
  display: flex;
  height: 40px;
  padding: 6px 8px 6px 12px;
  align-items: center;
  align-self: stretch;

  border-radius: 6px;
  border: 1px solid var(--RG06, #d8ddea);
  background: var(--RG00, #fff);

  color: var(--GrayScale-black, #000);
  /* subheadline2 */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 171.429% */
  letter-spacing: -0.28px;
`;

const SecondDiv = styled.div`
  display: flex;
  height: 432px;
  padding: 0px 20px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;
const SecondDivTitle = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;

  color: var(--RG02, #25282f);
  font-feature-settings: 'liga' off, 'clig' off;

  /* body16B */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px; /* 150% */
  letter-spacing: -0.32px;
`;
const SecondDivTitleDescriptionDiv = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  align-self: stretch;

  color: var(--GrayScale-gray800, #565b69);
  font-feature-settings: 'liga' off, 'clig' off;

  /* body14M */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 171.429% */
  letter-spacing: -0.28px;
`;
const SecondDivCententContainer = styled.div<{ isTextArea: boolean }>`
  display: flex;
  padding: 8px 10px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
  align-self: stretch;

  border-radius: 8px;
  border: 1px solid var(--RG06, #d8ddea);
  background: ${({ isTextArea, theme }) => (isTextArea ? theme.colors.RG00 : '#f9f9f9')};
`;
const SecondDivCententTextArea = styled.textarea`
  flex: 1;
  padding-top: 5px;
  height: 100%; /* 부모 컨테이너의 높이를 따르게 설정 */
  resize: none; /* 필요에 따라 크기 조정 기능을 비활성화 */
  //textarea 스크롤바 적용
  overflow-y: overlay;
  ${({ theme }) => scrollBarStyle()}

  /* body14M */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 171.429% */
  letter-spacing: -0.28px;
`;
const SecondDivOver90Text = styled.span`
  color: var(--Secondary-Red200, #ff4343);
  font-feature-settings: 'liga' off, 'clig' off;

  /* body14M */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 171.429% */
  letter-spacing: -0.28px;
`;
const LMSLabel = styled.span`
  display: flex;
  padding: 0px 8px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  margin-right: 8px;

  border-radius: 4px;
  border: 1px solid var(--Secondary-Red200, #ff4343);
  background: var(--Secondary-Red200, #ff4343);

  color: var(--Secondary-Red100, #fff1f1);

  /* Caption/CaptionSB12 */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 21px; /* 175% */
  letter-spacing: -0.24px;
`;

const ThirdDiv = styled.div`
  display: flex;
  padding: 0px 20px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;
const ThirdDivTitleDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;
const ThirdDivTitleText = styled.div`
  color: var(--RG02, #25282f);
  font-feature-settings: 'liga' off, 'clig' off;

  /* body16B */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px; /* 150% */
  letter-spacing: -0.32px;
`;
const ThirdDivTitleDescriptionText = styled.div`
  color: var(--GrayScale-gray800, #565b69);
  font-feature-settings: 'liga' off, 'clig' off;

  /* body14M */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 171.429% */
  letter-spacing: -0.28px;
`;
const ThirdDivDateContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`;
const ThirdDivDateInputContainer = styled.div<{ isDisabled: boolean }>`
  display: flex;
  padding: 8px 10px;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;

  border-radius: 8px;
  border: 1px solid var(--RG06, #d8ddea);
  background: var(--RG00, #fff);

  & > input {
    width: 100%;
    background: transparent;

    ::placeholder {
      color: ${({ theme }) => theme.colors.RG04};
      font-size: 13px;
    }

    color: var(--GrayScale-black, #000);

    /* subheadline2 */
    font-family: Pretendard;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 24px; /* 171.429% */
    letter-spacing: -0.28px;
  }
  ${({ isDisabled, theme }) => ({
    backgroundColor: isDisabled ? '#f9f9f9' : theme.colors.RG00,
  })}
`;
const CalanderIconContainer = styled.div<{ isActive: boolean; hasError: boolean }>`
  width: 18;
  height: 18;
  position: relative;
  background-color: #d8ddea
    ${({ isActive, hasError, theme }) => ({
      background: isActive ? theme.colors.RC03_1 : theme.colors.RG00,
    })};
`;
const TimeContaniner = styled.div<{ isDisabled: boolean }>`
  display: flex;
  width: 80px;
  height: 40px;
  padding: 8px 10px 8px 10px;
  align-items: center;
  gap: 10px;

  border-radius: 8px;
  border: 1px solid var(--RG06, #d8ddea);
  background: var(--RG00, #fff);
  background-color: ${({ isDisabled, theme }) => (isDisabled ? '#f9f9f9' : theme.colors.RG00)};
`;
const TimeInput = styled.input`
  background-color: inherit;
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  ::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  width: 30px;

  color: ${props => props.color};
  /* subheadline2 */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 171.429% */
  letter-spacing: -0.28px;

  /* padding: 8px 10px 8px 10px; */
`;
const TimeText = styled.div`
  width: 14px;
  z-index: 1;
  position: relative;

  color: var(--RG04, #8f96a9);

  /* subheadline2 */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 171.429% */
  letter-spacing: -0.28px;
`;
const ThirdCheckBoxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  color: var(--semantic-color-text-text-default, #1c1d23);

  /* Body/BodyR14 */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 25px; /* 178.571% */
  letter-spacing: -0.28px;
`;

const FirthDiv = styled.div`
  display: flex;
  padding: 0px 20px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;
const FirthDivTitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;
const FirthDivTitle = styled.div`
  color: var(--RG02, #25282f);
  font-feature-settings: 'liga' off, 'clig' off;

  /* body16B */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px; /* 150% */
  letter-spacing: -0.32px;
`;
const FirthDivDescription = styled.div`
  color: var(--GrayScale-gray800, #565b69);
  font-feature-settings: 'liga' off, 'clig' off;

  /* body14M */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 171.429% */
  letter-spacing: -0.28px;
`;
const FirthCustomerInfoContainer = styled.div`
  display: flex;
  padding-bottom: 12px;
  align-items: flex-start;
  gap: 8px;
  // TODO: 나중에 예기 나오면 여기서 height 를 넣어줘야 함
  flex-wrap: wrap;
  align-content: flex-start;

  overflow-y: overlay;
  ${({ theme }) => scrollBarStyle()}
`;
const FirthCustomerInfoItem = styled.div`
  display: flex;
  padding: 2px 8px 2px 10px;
  justify-content: center;
  align-items: center;
  gap: 6px;

  border-radius: 4px;
  border: 1px solid var(--RG05, #b2b8cc);
  background: var(--RG00, #fff);

  color: var(--RG03, #565b69);
  text-align: center;

  /* caption1 */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px; /* 166.667% */
  letter-spacing: -0.24px;
`;
